﻿import { Extent, getHeight, getWidth } from "ol/extent";
import Geometry from "ol/geom/Geometry";
import { Size } from "ol/size";
import View from "ol/View";
import HasViewFitExtensions, { FitOptions } from "../extensions/HasViewFitExtensions";
import GeometryConverter from "../geometries/GeometryConverter";

const DEFAULT_OPTIONS: FitOptions = {
    smartFit: false,
    relativeZoomAdjustment: 0.9
}

class ViewUtilities {

    public static getZoomFromResolution(resolution: number) {
        return Math.log((40075008 / 256) / resolution) / Math.log(2);
    }

    public static getResolutionFromZoom(zoom: number) {
        return (40075008 / 256) / (Math.pow(2, zoom));
    }

    public static getResolutionForExtent(extent: Extent, size: Size) {
        const xResolution = getWidth(extent) / size[0];
        const yResolution = getHeight(extent) / size[1];
        return Math.max(xResolution, yResolution);
    }

    public static getZoomForExtent(extent: Extent, size: Size) {
        return this.getZoomFromResolution(this.getResolutionForExtent(extent, size));
    }

    public static nearestZoomResolution(resolution: number): number {
        return this.getResolutionFromZoom(Math.round(this.getZoomFromResolution(resolution)));
    }

    public static fitExtents(view: View, extents: Extent[], boundary?: Extent[], options?: FitOptions) {
        const extension = new HasViewFitExtensions(view);

        options = {
            ...DEFAULT_OPTIONS,
            ...options,
        }

        const max = boundary ? this.getZoomLevelForExtents(extension, boundary, options) : undefined;

        extension.fitExtents(extents, {
            ...options,
            pointZoom: max,
            maxZoom: max
        })
    }

    public static fitGeojson(view: View, geojson: GeoJSON.GeoJSON | GeoJSON.GeoJSON[], boundary?: GeoJSON.GeoJSON | GeoJSON.GeoJSON[], options?: FitOptions) {
        const extension = new HasViewFitExtensions(view);

        options = {
            ...DEFAULT_OPTIONS,
            ...options,
        }

        const max = boundary ? this.getZoomLevelForExtents(extension, this.getGeometriesFromGeoJSON(boundary), options) : undefined;

        extension.fitGeoJson(geojson, {
            ...options,
            pointZoom: max,
            maxZoom: max
        });
    }

    private static getZoomLevelForExtents(extension: HasViewFitExtensions, extents: Extent[] | Geometry[], options?: FitOptions) {
        const extent = extension.getExtent(extents);
        if (extent)
            return extension.getFitZoomLevel(extent, options);
    }

    private static getGeometriesFromGeoJSON(geojson: GeoJSON.GeoJSON | GeoJSON.GeoJSON[]) {
        const geometries: Geometry[] = [];
        const geoJsons = geojson instanceof Array ? geojson : [geojson];

        geoJsons.forEach(g => {
            const geometry = GeometryConverter.getOpenlayersGeometry(g);
            if (geometry)
                geometries.push(geometry);
        })

        return geometries;
    }
}

export default ViewUtilities;